import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { Subscription, firstValueFrom } from 'rxjs';
import { OrganizationAccessDialogComponent, OrganizationAccessDialogData } from 'src/app/dialogs/organization-access-dialog/organization-access-dialog.component';
import { Organization } from 'src/app/models/organization';
import { SessionAccess } from 'src/app/models/organizationAccess';
import { Role, User } from 'src/app/models/user';
import { AclService } from 'src/app/services/acl.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { MessageService } from 'src/app/services/message.service';
import { OrganizationAccessService } from 'src/app/services/organization-access.service';
import { OrganizationQuotas, OrganizationService } from 'src/app/services/organization.service';

const ACCESS_BANNER_HIDDEN_KEY = 'uzai-access-banner-hidden';

function readHiddenAccessId(): string | undefined {
  try {
    return sessionStorage.getItem(ACCESS_BANNER_HIDDEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

interface NavSection {
  label: string;
  icon: string;
  path: string;
  active: boolean;
  roles: (keyof typeof Role)[];
  guards?: (() => boolean)[];
  badge?: number;
}

interface NavSections {
  top: NavSection[];
  bottom: NavSection[];
  admin: NavSection[];
}

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss']
})
export class NavComponent implements OnInit {

  loading: boolean = false;

  readonly sections: NavSections = {
    top: [
      {
        label: 'NAV.SECTIONS.HOME',
        icon: 'home',
        path: '',
        active: true,
        roles: []
      },
      {
        label: 'NAV.SECTIONS.TRADEMARKS',
        icon: 'account_balance_wallet',
        path: 'trademarks',
        active: false,
        roles: ['SUPERADMIN', 'ADMIN', 'MANAGER']
      },
      {
        label: 'NAV.SECTIONS.DOCUMENTS',
        icon: 'description',
        path: 'documents',
        active: false,
        guards: [() => this.aclService.isManager()],
        roles: ['SUPERADMIN', 'ADMIN', 'MANAGER', 'COUNTRY_OPERATOR', 'OPERATOR']
      },
      {
        label: 'NAV.SECTIONS.CASES',
        icon: 'folder',
        path: 'cases',
        active: false,
        guards: [() => this.aclService.isManager()],
        roles: ['SUPERADMIN', 'ADMIN', 'MANAGER', 'CASE_OPERATOR']
      },
      {
        label: 'NAV.SECTIONS.INTEGRATIONS_WEBSITE',
        icon: 'language',
        path: 'integrations/website',
        active: false,
        guards: [() => this.aclService.isManager()],
        roles: ['SUPERADMIN', 'ADMIN', 'MANAGER']
      }
    ],
    bottom: [
      {
        label: 'NAV.SECTIONS.SUPPORT',
        icon: 'contact_support',
        path: 'support',
        active: false,
        roles: []
      },
      {
        label: 'NAV.SECTIONS.ORGANIZATION',
        icon: 'business',
        path: 'organization',
        active: false,
        roles: [],
        badge: undefined
      }
    ],
    admin: [
      {
        label: 'NAV.SECTIONS.ADMIN',
        icon: 'dashboard',
        path: 'admin',
        active: false,
        roles: ['SUPERADMIN']
      }
    ]
  };


  uploadPanel: boolean = false;

  user?: User;

  /** A leave asked from the banner is in flight. */
  leavingAccess: boolean = false;

  /**
   * The access whose banner was hidden, for a demo in front of the client: only a discreet arrow remains. Shown
   * again by any new access (entering, changing role); remembered for this tab.
   */
  hiddenAccessId: string | undefined = readHiddenAccessId();

  organization?: Organization;
  quotas?: OrganizationQuotas;

  readonly subscription: Subscription;


  readonly positions = [
    new ConnectionPositionPair(
      { originX: 'end', originY: 'top' },
      { overlayX: 'start', overlayY: 'center' }
    ),
  ];

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _message: MessageService,
    private readonly aclService: AclService,
    private readonly authService: AuthenticationService,
    private readonly dialog: MatDialog,
    private readonly organizationAccessService: OrganizationAccessService,
    private readonly organizationService: OrganizationService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService
  ) {
    this.authService.user.subscribe((user: User | undefined) => {
      this.user = user;
    });
    this.organizationService.quotasObs.subscribe((quotas: OrganizationQuotas | undefined) => {
      this.quotas = quotas;
      if (this.quotas && this.aclService.isAdmin()) {
        this.sections.bottom.forEach((section) => {
          if (section.path == 'organization') {
            section.badge = (this.quotas && this.quotas?.exceeded > 0) ? this.quotas?.exceeded : undefined;
          }
        })
      }
    });

    this.subscription = this._message.changeEmitted$.subscribe(change => {
      if (change && change.key == "NAV.SECTION") {
        this._active(change.data.path)
      }
      if (change && change.key == "LOADING.START") {
        this.loading = true;
        this._cd.detectChanges();
      }
      if (change && change.key == "LOADING.END") {
        this.loading = false;
        this._cd.detectChanges();
      }
      if (change && change.key == "UPLOAD.SHOW") {
        this.showUploadPanel();
      }
      if (change && change.key == "UPLOAD.HIDE") {
        this.hideUploadPanel();
      }
    });
  }

  async ngOnInit() {
    void this.announceEndedAccess();
    await this.organizationService.mine();
  }

  /** After the reload that follows an access ended by the backend, says so once. */
  private async announceEndedAccess() {
    const organization = this.organizationAccessService.takeEndedNotice();
    if (organization === undefined) {
      return;
    }
    const message = await firstValueFrom(this.translate.get(
      organization ? 'ACCESS.ENDED' : 'ACCESS.ENDED_UNKNOWN', { organization }
    ));
    this.toastr.info(message, undefined, { timeOut: 10000 });
  }

  /** « You are at X as Y, scope: N cases — until HH:mm », for the banner of an organisation access. */
  accessBannerText(access: SessionAccess): string {
    const count = access.constraint?.values?.length ?? 0;
    const scope = count > 0
      ? this.translate.instant(access.constraint?.name === 'CASE' ? 'ACCESS.BANNER_SCOPE_CASES' : 'ACCESS.BANNER_SCOPE_COUNTRIES', { count })
      : '';
    return this.translate.instant('ACCESS.BANNER', {
      organization: access.organization.name,
      role: this.translate.instant(`USER_ROLES.${access.role}`),
      scope,
      until: moment(access.expiresAt).format('HH:mm')
    });
  }

  hideAccessBanner(access: SessionAccess, hidden: boolean) {
    this.hiddenAccessId = hidden ? access.id : undefined;
    try {
      if (hidden) {
        sessionStorage.setItem(ACCESS_BANNER_HIDDEN_KEY, access.id);
      } else {
        sessionStorage.removeItem(ACCESS_BANNER_HIDDEN_KEY);
      }
    } catch {
      // A convenience only: without storage, the banner shows again after a reload.
    }
  }

  changeAccessRole(access: SessionAccess) {
    const data: OrganizationAccessDialogData = { organization: access.organization, access };
    this.dialog.open(OrganizationAccessDialogComponent, {
      panelClass: 'dialog-container',
      width: '520px',
      data
    });
  }

  async leaveAccess() {
    if (this.leavingAccess) return;
    this.leavingAccess = true;
    this._message.emitChange("LOADING", "START");
    try {
      // On success the page reloads on the dashboard of the organisation left.
      await this.organizationAccessService.leave();
    } catch (err) {
      this.leavingAccess = false;
      this._message.emitChange("LOADING", "END");
      if (!(err instanceof HttpErrorResponse) || err.status === 401 || err.status === 403) {
        // No refresh token, refresh token no longer valid, or account suspended: nothing to go back to.
        this.authService.logout();
      } else {
        this.toastr.error('ERRORS.GENERIC');
      }
    }
  }

  _active(path: string) {
    const setActive = (section: NavSection) => {
      section.active = section.path === path;
    };
    this.sections.top.forEach(setActive);
    this.sections.bottom.forEach(setActive);
    this.sections.admin.forEach(setActive);
    this._cd.detectChanges();
  }

  showUploadPanel() {
    this.uploadPanel = true;
  }

  hideUploadPanel() {
    this.uploadPanel = false;
  }

  logout() {
    this.authService.logout();
  }

  toSettings() {
    this.router.navigate(['settingss'])
  }
}
